import { restaurantApi } from '@noor/eats-api';
import { toDaySchedule, toVenue } from '../lib/map-venue';
import type { Venue, VenueHours } from '../model/schema';

/** Ids needed to address one branch's profile. */
export interface VenueScope {
  restaurantId: string;
}

/** Partial write payload for the venue profile (only working hours today). */
export interface VenueUpdateInput {
  workingHours: VenueHours[];
}

/**
 * A schedule save's result: the (re-read) profile, plus whether the write was
 * QUEUED behind moderation rather than applied — a gated merchant's PATCH
 * answers with a receipt, and the stored schedule stays unchanged until a
 * moderator approves it.
 */
export interface VenueUpdateResult {
  venue: Venue;
  pending: boolean;
}

export const venueApi = {
  /**
   * The branch profile: its record plus its weekly schedule, both returned by
   * one call (the schedule rides alongside `data.restaurant` as a sibling
   * `list` — the dedicated `.../working-hours` GET is deprecated).
   */
  async get(scope: VenueScope, options?: { signal?: AbortSignal }): Promise<Venue> {
    const { restaurant, schedule } = await restaurantApi.getWithSchedule(
      scope.restaurantId,
      options,
    );
    return toVenue(restaurant, schedule);
  },

  /**
   * Replace the weekly schedule — via `PATCH /restaurants/{id}`'s
   * `week_schedule` field (the dedicated `PUT .../working-hours` endpoint was
   * removed from the gateway). The profile is re-read to rebuild the view
   * model; on a gated write the re-read returns the still-unchanged schedule,
   * which is exactly what the merchant's screen should show while the change
   * awaits review.
   */
  async update(scope: VenueScope, input: VenueUpdateInput): Promise<VenueUpdateResult> {
    const outcome = await restaurantApi.setWorkingHours(
      scope.restaurantId,
      toDaySchedule(input.workingHours),
    );
    const { restaurant, schedule: stored } = await restaurantApi.getWithSchedule(
      scope.restaurantId,
    );
    const schedule = outcome.status === 'applied' ? outcome.schedule : stored;
    return {
      venue: toVenue(restaurant, schedule),
      pending: outcome.status === 'pending',
    };
  },
};
